import { Component, Input } from '@angular/core';
import { FormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-transaction-edit-modal',
  templateUrl: './transaction-edit-modal.component.html',
  styleUrls: ['./transaction-edit-modal.component.scss']
})
export class TransactionEditModalComponent {

  @Input() transaction!: any;
  form: UntypedFormGroup;

  readonly types = [
    { value: 'pending',   i18n: 'company.transaction.filters.type-pending' },
    { value: 'payment',   i18n: 'company.transaction.filters.type-payment' },
    { value: 'refund',    i18n: 'company.transaction.filters.type-refund' },
    { value: 'cancelled', i18n: 'company.transaction.filters.type-cancelled' },
    { value: 'pendingCancel', i18n: 'company.transaction.filters.pending-cancelled' },
  ];

  get breakdown(): FormArray {
    return this.form.get('breakdown') as FormArray;
  }

  constructor(private fb: UntypedFormBuilder, public activeModal: NgbActiveModal) {
    this.form = this.fb.group({
      type: ['', Validators.required],
      order: [''],
      amount: [{ value: '', disabled: true }],
      breakdown: this.fb.array([]),
    });
  }

  ngOnInit() {
    const initialType = (this.transaction?.type || '');
    const service = (this.transaction?.service || '').toLowerCase();
    const bookeds = Array.isArray(this.transaction?.bookedObject) ? this.transaction.bookedObject : [];

    const refundMap = new Map<string, number>();
    if (Array.isArray(this.transaction?.refundBreakdown)) {
      for (const r of this.transaction.refundBreakdown) {
        refundMap.set(String(r.uuid), Number(r.amount || 0));
      }
    }

    const singleEntity = bookeds.length === 1;

    const rows = bookeds.map((b: any) => {
      const uuid = b?.uuid as string;
      let label = '';
      let currentBase = 0;

      if (service === 'flight') {
        label = `${b?.from ?? ''} - ${b?.to ?? ''}`.trim();
        currentBase = Number(b?.price?.individualPrice ?? 0);
      } else if (service === 'train') {
        label = `${b?.from ?? ''} - ${b?.to ?? ''}`.trim();
        currentBase = Number(b?.price ?? 0);
      } else if (service === 'room') {
        label = `${b?.hotel?.name ?? b?.hotelName ?? ''} - ${b?.hotel?.destinationName ?? b?.destinationName ?? ''}`.trim();
        currentBase = Number(b?.price ?? 0);
      } else if (service === 'car') {
        label = `${b?.from ?? ''} - ${b?.to ?? ''}`.trim();
        currentBase = Number(b?.price ?? 0);
      } else if (service === 'expense'){
        label = `${b?.type ?? ''}`;
        currentBase = Number(b?.price ?? 0);
      }

      const passenger = this.resolvePassengerName(b, service);

      let initialValue = currentBase;

      if (initialType === 'refund') {
        if (refundMap.has(uuid)) {
          initialValue = Number(refundMap.get(uuid) || 0);
        } else {
          initialValue = singleEntity ? Number(this.transaction?.amount || 0) : 0;
        }
      } else if (initialType === 'payment') {
        initialValue = currentBase;
      }

      return { uuid, label, passenger, base: currentBase, amount: initialValue };
    });

    if (initialType === 'pending' && bookeds.length > 0) {
      const hasRefundHistory = (Array.isArray(this.transaction?.refundBreakdown) && this.transaction.refundBreakdown.length > 0);
      const targetTotal = Number(this.transaction?.amount ?? 0);
      if (hasRefundHistory) {
        rows.forEach(r => {
          r.amount = Number(refundMap.get(r.uuid) ?? 0);
        });
      } else {
        const bases = rows.map(r => r.base);
        const baseSum = bases.reduce((a, b) => a + (Number(b) || 0), 0);
        let distributed: number[];
        if (targetTotal > 0) {
          distributed = baseSum > 1e-9
            ? this.distributeProportionally(bases, targetTotal)
            : this.distributeEqually(rows.length, targetTotal);
        } else {
          distributed = Array(rows.length).fill(0);
        }
        rows.forEach((r, i) => { r.amount = distributed[i]; });
      }
    }

    rows.forEach(r => {
      this.breakdown.push(this.fb.group({
        uuid: [r.uuid],
        label: [r.label],
        passenger: [r.passenger],
        amount: [
          this.formatAmount(r.amount),
          [Validators.required, Validators.pattern(/^\d+([.,]\d{1,2})?$/)]
        ],
      }));
    });

    this.form.patchValue({
      type: initialType,
      order: this.transaction?.order || ''
    });

    if (initialType === 'pending') {
      const targetTotal = Number(this.transaction?.amount ?? 0);
      this.form.get('amount')?.setValue(targetTotal.toFixed(2), { emitEvent: false });
    } else {
      this.recomputeTotal();
    }

    this.breakdown.valueChanges.subscribe(() => this.recomputeTotal());
  }


  private distributeProportionally(bases: number[], target: number): number[] {
    const baseSum = bases.reduce((a, b) => a + (Number(b) || 0), 0);
    if (baseSum <= 1e-12) return this.distributeEqually(bases.length, target);

    const raw = bases.map(b => (target * (Number(b) || 0)) / baseSum);
    const rounded = raw.map(v => Math.round(v * 100) / 100);
    let diff = Math.round((target - rounded.reduce((a, b) => a + b, 0)) * 100) / 100;
    if (Math.abs(diff) > 1e-12) {
      rounded[rounded.length - 1] = Math.round((rounded[rounded.length - 1] + diff) * 100) / 100;
    }
    return rounded;
  }

  private distributeEqually(n: number, target: number): number[] {
    if (n <= 0) return [];
    const each = Math.floor((target / n) * 100) / 100;
    const arr = Array(n).fill(each);
    const sum = arr.reduce((a, b) => a + b, 0);
    const diff = Math.round((target - sum) * 100) / 100;
    arr[n - 1] = Math.round((arr[n - 1] + diff) * 100) / 100;
    return arr;
  }

  private resolvePassengerName(b: any, service: string): string | null {
    if (!b) return null;
    if (service !== 'flight' && service !== 'train') return null;

    const n1 = b?.extraInfo?.[0]?.passengerName || b?.extraInfo?.[0]?.passengerFullName ;
    if (n1) return n1;

    const n2 = b?.passengerName || b?.passengerFullName  || b?.travellerName || b?.holderName;
    if (n2) return n2;

    const paxArr: any[] =
      Array.isArray(b?.passengers) ? b.passengers :
      Array.isArray(b?.travellers) ? b.travellers : [];

    if (paxArr.length) {
      const first = paxArr[0];
      const guess = first?.fullName || [first?.name, first?.surname].filter(Boolean).join(' ');
      if (guess) return guess;
    }
    return null;
  }

  private parseEur(val: any): number {
    const str = (val ?? '').toString().replace(',', '.').trim();
    const n = parseFloat(str);
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
  }

  private formatAmount(n: number): string {
    return (Number(n || 0)).toFixed(2);
  }

  private recomputeTotal() {
    const sum = (this.breakdown.controls || []).reduce((acc, ctrl) => {
      const v = this.parseEur(ctrl.get('amount')?.value);
      return acc + (Number.isFinite(v) ? v : 0);
    }, 0);
    this.form.get('amount')?.setValue(sum.toFixed(2), { emitEvent: false });
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.getRawValue();
    const amountNum = this.parseEur(v.amount);
    const type = (v.type || '').trim();

    const items = (v.breakdown || [])
      .map((row: any) => ({
        uuid: row.uuid,
        amount: this.parseEur(row.amount),
      }))
      .filter((x: any) => Number.isFinite(x.amount));

    const payload: any = {
      type,
      amount: amountNum,
      editBreakdown: items,
    };

    const newOrder = (v.order || '').trim();
    if (newOrder !== (this.transaction?.order || '')) {
      payload.order = newOrder;
    }

    if (type === 'refund') payload.refundBreakdown = items;
    else if (type === 'payment') payload.paymentBreakdown = items;

    this.activeModal.close(payload);
  }

  normalizeEmpty(i: number) {
    const ctrl = (this.breakdown.at(i) as UntypedFormGroup).get('amount');
    if (!ctrl) return;

    const raw = (ctrl.value ?? '').toString().trim();

    if (raw === '' || raw === ',' || raw === '.' ) {
      ctrl.setValue('0');
    }
  }

  cancel() {
    this.activeModal.dismiss();
  }

}
